import React, { useEffect, useRef } from 'react';
import { Text } from 'src/@vymo/ui/atoms';
import classnames from 'classnames';
import { ActivityListItemProps } from './types';
import styles from './index.module.scss';

function ActivityListItem({
  activity,
  isSelected,
  onClick,
}: ActivityListItemProps) {
  const itemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isSelected || !itemRef.current) return;

    const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
      let parent = element.parentElement;
      while (parent) {
        const { overflowY } = window.getComputedStyle(parent);
        if (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          parent.scrollHeight > parent.clientHeight
        ) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const scrollableParent = findScrollableParent(itemRef.current);
    if (!scrollableParent) return;

    const itemRect = itemRef.current.getBoundingClientRect();
    const parentRect = scrollableParent.getBoundingClientRect();
    const scrollTop =
      itemRect.top -
      parentRect.top +
      scrollableParent.scrollTop -
      scrollableParent.clientHeight / 2 +
      itemRect.height / 2;

    scrollableParent.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }, [isSelected]);

  const getColorIndicatorClass = () => {
    const isCompleted = activity?.meeting?.is_completed;
    const meetingDate = new Date(activity?.meeting?.date);
    const state = activity?.calender_item?.state;
    const now = new Date();

    let status = '';

    if (isCompleted === true) {
      status = 'completed';
    } else if (isCompleted === false && state === 'OPEN') {
      if (meetingDate.getTime() < now.getTime()) {
        status = 'pending';
      } else {
        status = 'planned';
      }
    }

    return status
      ? styles[`activityListItem__details__${status}`]
      : styles.activityListItem__details__default;
  };

  const startDate = new Date(activity.meeting.date);
  const endDate = activity.meeting.duration
    ? new Date(startDate.getTime() + activity.meeting.duration)
    : null;

  const formatTime12hr = (date: Date) =>
    date
      .toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .replace('am', 'AM')
      .replace('pm', 'PM');

  return (
    <div
      ref={itemRef}
      className={classnames(styles.activityListItem, {
        [styles.activityListItem__selected]: isSelected,
      })}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className={styles.activityListItem__time}>
        <Text>{formatTime12hr(startDate)}</Text>
        <Text>{endDate ? formatTime12hr(endDate) : '-'}</Text>
      </div>
      <div
        className={classnames(
          styles.activityListItem__details,
          getColorIndicatorClass(),
        )}
      >
        <Text>{activity.name}</Text>
        {activity?.user?.name && (
          <Text type="sublabel">{activity.user.name}</Text>
        )}
      </div>
    </div>
  );
}

export default ActivityListItem;
